import { useCallback } from 'react';
import { type Query, useQueryClient } from '@tanstack/react-query';
import {
  LATEST_STEP_RUNS_BASE_KEY,
  RETRY_PRESENTATION_BASE_KEY,
  RUN_AUTO_EXPAND_PLAN_KEY,
  STEP_EXECUTION_KEY,
  WAITING_STEP_DEPENDENCIES_BASE_KEY,
} from '@/utils/queryKeys.ts';
import { isQueryMountedWithData } from '@/hooks/api/sse/utils.ts';
import { StepExecution, StepsRuns } from '@/types/stepExecutions.ts';

const isLatestStepRunsQuery = (query: Query, automationId: string, runId: string) => {
  const baseKey = LATEST_STEP_RUNS_BASE_KEY(automationId, runId);
  return baseKey.every((part, index) => query.queryKey[index] === part);
};

const useInvalidateStepExecution = (automationId: string, runId: string) => {
  const queryClient = useQueryClient();

  return useCallback(
    (stepExecution: StepExecution): unknown[][] => {
      const queryKey = STEP_EXECUTION_KEY(automationId, stepExecution.id);

      const queryIsMountedWithData = isQueryMountedWithData(queryClient, queryKey);
      queryClient.setQueriesData<StepsRuns>(
        {
          predicate: (query) => isLatestStepRunsQuery(query, automationId, runId),
        },
        (data) => {
          if (!data) return data;

          let changed = false;
          const stepRunsByStepId = Object.fromEntries(
            Object.entries(data.step_runs_by_step_id).map(([stepId, stepRuns]) => [
              stepId,
              stepRuns.map((stepRun) => {
                if (stepRun.step_execution_id !== stepExecution.id) return stepRun;
                changed = true;
                return { ...stepRun, step_execution: stepExecution };
              }),
            ]),
          );

          return changed ? { ...data, step_runs_by_step_id: stepRunsByStepId } : data;
        },
      );

      if (!queryIsMountedWithData) {
        return [
          queryKey,
          RETRY_PRESENTATION_BASE_KEY(automationId, runId),
          WAITING_STEP_DEPENDENCIES_BASE_KEY(automationId, runId),
          RUN_AUTO_EXPAND_PLAN_KEY(automationId, runId),
        ];
      }

      queryClient.setQueryData<StepExecution>(queryKey, stepExecution);
      return [
        RETRY_PRESENTATION_BASE_KEY(automationId, runId),
        WAITING_STEP_DEPENDENCIES_BASE_KEY(automationId, runId),
        RUN_AUTO_EXPAND_PLAN_KEY(automationId, runId),
      ];
    },
    [automationId, queryClient, runId],
  );
};

export default useInvalidateStepExecution;
